import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Space,
  Modal,
  Tag,
  Tooltip,
  Popconfirm,
  Input,
  Collapse
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  AppstoreOutlined,
  ThunderboltOutlined,
  CheckOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import {
  fetchRbacActions,
  fetchActionsGrouped,
  createRbacAction,
  updateRbacAction,
  deleteRbacAction,
  setRbacEditing,
  clearRbacEditing
} from '../../redux/actions/rbacActions';
import ActionForm from './ActionForm';

const { Search } = Input;
const { Panel } = Collapse;

const ActionsTab = () => {
  const dispatch = useDispatch();
  const { actions, actionsGrouped, actionsLoading } = useSelector(state => state.rbac);
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grouped'

  useEffect(() => {
    dispatch(fetchRbacActions());
    dispatch(fetchActionsGrouped());
  }, [dispatch]);

  const handleAddNew = () => {
    dispatch(clearRbacEditing());
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    dispatch(setRbacEditing(record));
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    dispatch(deleteRbacAction(id));
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    dispatch(clearRbacEditing());
  };

  const handleRefresh = () => {
    dispatch(fetchRbacActions());
    dispatch(fetchActionsGrouped());
  };

  const filteredActions = actions.filter(action =>
    action.action.toLowerCase().includes(searchText.toLowerCase()) ||
    action.display_name.toLowerCase().includes(searchText.toLowerCase()) ||
    action.type.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action) => (
        <code style={{ fontSize: '12px', background: '#f5f5f5', padding: '2px 6px', borderRadius: '4px' }}>
          {action}
        </code>
      ),
    },
    {
      title: 'Display Name',
      dataIndex: 'display_name',
      key: 'display_name',
      render: (name) => <strong>{name}</strong>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (type) => (
        <Tag color="blue" className="action-type-badge">
          {type}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'enable',
      key: 'enable',
      width: 100,
      render: (enable) => (
        <Tag color={enable ? 'success' : 'error'}>
          {enable ? 'Enabled' : 'Disabled'}
        </Tag>
      ),
    },
    {
      title: 'Created On',
      dataIndex: 'created_on',
      key: 'created_on',
      width: 180,
      render: (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Delete Action"
            description="Are you sure you want to delete this action?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Calculate unique types
  const uniqueTypes = [...new Set(actions.map(a => a.type))];

  return (
    <div style={{ padding: '1rem' }}>
      {/* Stats Cards */}
      <div className="rbac-stats-grid">
        <div className="rbac-stat-card">
          <div className="rbac-stat-icon"><ThunderboltOutlined /></div>
          <div className="rbac-stat-content">
            <div className="rbac-stat-value">{actions.length}</div>
            <div className="rbac-stat-label">Total Actions</div>
          </div>
        </div>
        <div className="rbac-stat-card" style={{ borderLeftColor: '#28a745' }}>
          <div className="rbac-stat-icon" style={{ background: '#d4edda', color: '#155724' }}><CheckOutlined /></div>
          <div className="rbac-stat-content">
            <div className="rbac-stat-value">{actions.filter(a => a.enable).length}</div>
            <div className="rbac-stat-label">Enabled</div>
          </div>
        </div>
        <div className="rbac-stat-card" style={{ borderLeftColor: '#17a2b8' }}>
          <div className="rbac-stat-icon" style={{ background: '#d1ecf1', color: '#0c5460' }}><FolderOutlined /></div>
          <div className="rbac-stat-content">
            <div className="rbac-stat-value">{uniqueTypes.length}</div>
            <div className="rbac-stat-label">Module Types</div>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="modern-table-container" style={{ marginTop: '1.5rem' }}>
        <div className="table-header">
          <h2 className="table-title">Actions Management</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <Button
              onClick={() => setViewMode(viewMode === 'table' ? 'grouped' : 'table')}
            >
              {viewMode === 'table' ? 'Grouped View' : 'Table View'}
            </Button>
            <button className="btn btn-secondary" onClick={handleRefresh}>
              <ReloadOutlined /> Refresh
            </button>
            <button className="btn btn-primary" onClick={handleAddNew}>
              <PlusOutlined /> Add New Action
            </button>
          </div>
        </div>

        {/* Search */}
        <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px', marginBottom: '1rem' }}>
          <Search
            placeholder="Search by action, display name, or type"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            size="large"
          />
        </div>

        {viewMode === 'table' ? (
          <Table
            columns={columns}
            dataSource={filteredActions}
            rowKey="id"
            loading={actionsLoading}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} actions`,
            }}
            scroll={{ x: 1200 }}
            locale={{
              emptyText: (
                <div className="empty-state">
                  <div className="empty-state-icon"><ThunderboltOutlined /></div>
                  <div className="empty-state-text">No actions found</div>
                  <button className="btn btn-primary" onClick={handleAddNew}>
                    Add Your First Action
                  </button>
                </div>
              ),
            }}
          />
        ) : (
          <Collapse accordion>
            {actionsGrouped.map(group => (
              <Panel
                header={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>
                      <AppstoreOutlined /> {group.type}
                    </span>
                    <Tag color="blue">{group.actions.length} actions</Tag>
                  </div>
                }
                key={group.type}
              >
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {group.actions.map(action => (
                    <div key={action.id} className="permission-item">
                      <div className="permission-item-info">
                        <div className="permission-item-name">{action.display_name}</div>
                        <div className="permission-item-action">{action.action}</div>
                      </div>
                      <Space>
                        <Tag color={action.enable ? 'success' : 'error'}>
                          {action.enable ? 'Enabled' : 'Disabled'}
                        </Tag>
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          onClick={() => handleEdit(action)}
                          size="small"
                        />
                        <Popconfirm
                          title="Delete Action"
                          description="Are you sure?"
                          onConfirm={() => handleDelete(action.id)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                          />
                        </Popconfirm>
                      </Space>
                    </div>
                  ))}
                </div>
              </Panel>
            ))}
          </Collapse>
        )}
      </div>

      {/* Action Form Modal */}
      <Modal
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        destroyOnClose
        centered
        title="Action Form"
      >
        <ActionForm onCancel={handleModalClose} />
      </Modal>
    </div>
  );
};

export default ActionsTab;
